import { useEffect, useMemo, useState } from 'react'

import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'

import { SummaryItem } from 'domains/Workspaces/Events/ConfigSummaryCells'
import { videoStreamQueryKeys } from 'services/queryKeys'

/** Ids of the streams feeding an event's cameras, in camera order (a camera may have none). */
export const readCameraStreamIds = (event: SmepEvent): (string | undefined)[] => {
  const tracksById = new Map((event.config?.videoTracks ?? []).map((track) => [track.id, track]))

  return (event.config?.entities ?? []).map((entity) => {
    const trackId = entity.videoContents?.[0]?.videoLinks?.[0]?.trackId
    const track = trackId != null ? tracksById.get(trackId) : undefined
    return track?.streamSources?.[0]?.streamId
  })
}

/**
 * Ids per request. The whole backlog goes into one POST body, so it is capped rather than sent
 * whole — a long scroll through the virtualized list can leave hundreds of cameras unresolved.
 * The queue drains itself: resolved ids drop out of `missingIds`, so the next render asks for the
 * next slice until nothing is left.
 */
const BATCH_LIMIT = 50

/**
 * Resolves the camera streams of the given events to their names.
 *
 * The table names cameras by their stream and the config only carries ids, so they are looked up
 * in batches instead of one detail query per camera the way the event page does it. Only ids
 * that are still unknown are requested, so loading another page of events asks for the streams it
 * introduced rather than for the whole list again. Ids that do not resolve are kept as empty names
 * — otherwise they would count as unknown forever and stay pending.
 */
export const useCameraStreamNames = (events?: SmepEvent[]) => {
  const [names, setNames] = useState<Map<string, string>>(new Map())

  const streamIds = useMemo(
    () => [...new Set((events ?? []).flatMap(readCameraStreamIds).filter((id) => id != null))],
    [events],
  )

  const missingIds = useMemo(() => streamIds.filter((id) => !names.has(id)).sort(), [streamIds, names])
  const requestedIds = useMemo(() => missingIds.slice(0, BATCH_LIMIT), [missingIds])

  const batchQuery = useQuery({
    ...videoStreamQueryKeys.batchGet({ videoStreamBatchGet: { ids: requestedIds } }),
    enabled: !!requestedIds.length,
    staleTime: Infinity,
  })
  const result = batchQuery.data

  useEffect(() => {
    if (!result) {
      return
    }

    setNames((current) => {
      const next = new Map(current)
      /*
       * Every requested id gets an entry up front, so the ones the batch could not resolve — not
       * found, forbidden, or landed in `errors`, which carries no ids to read them back from —
       * settle as nameless instead of staying pending forever behind a skeleton.
       */
      for (const id of requestedIds) {
        next.set(id, '')
      }
      for (const stream of result.items) {
        next.set(stream.id, stream.name)
      }
      return next
    })
  }, [result, requestedIds])

  return names
}

/*
 * Built in the cell renderer rather than in an `accessorFn`: the table caches accessor results per
 * row (`row._valuesCache`) and only drops that cache when the row model is rebuilt, so a name
 * arriving after the first render would never reach the cell.
 */
export const buildCameraSummary = (event: SmepEvent, names: Map<string, string>): SummaryItem[] => {
  const streamIds = readCameraStreamIds(event)

  return (event.config?.entities ?? []).map((entity, index) => {
    const streamId = streamIds[index]
    return {
      name: streamId ? (names.get(streamId) ?? '') : '',
      enabled: !entity.disable,
      secondary: entity.name || undefined,
      pending: !!streamId && !names.has(streamId),
    }
  })
}
